// Option H — what is missing, while the participant is still in the room.
// A domain scores null at 7 of 8 answers, which looks identical to one never
// started. Separating the two turns a gap into something fixable in the
// consultation instead of a blank discovered at the end of the programme.
import React from "react";
import { useTranslation } from "react-i18next";
import { Section } from "@/components/clinical/kit";
import { completeness } from "@/config/clinical";

export default function CompletenessBar({ responses, onOpenWorksheet }) {
  const { t } = useTranslation();
  const c = completeness(responses);
  const pct = Math.round((c.scoredCount / c.total) * 100);

  return (
    <Section title="Assessment completeness" sub="Scored domains only" testid="completeness">
      <div className="flex items-center gap-3">
        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-stone-100">
          <div className="h-full rounded-full bg-emerald-700 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="font-mono text-sm tabular-nums text-stone-700" data-testid="completeness-count">
          {c.scoredCount}/{c.total}
        </span>
      </div>

      {c.partial.length > 0 && (
        <div className="mt-3" data-testid="partial-domains">
          <p className="text-sm font-medium text-sky-800">
            {c.partial.length}{" "}
            {t(c.partial.length === 1
              ? "domain is part-answered and therefore unscored — finish it during the consultation:"
              : "domains are part-answered and therefore unscored — finish them during the consultation:")}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {c.partial.map((d) => (
              <button key={d.wid} type="button" onClick={() => onOpenWorksheet?.(d.wid)}
                data-testid={`partial-${d.wid}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs text-sky-800 hover:bg-sky-100">
                <span className="font-mono text-[10px] text-sky-500">{d.num}</span>
                {t(d.label)}
                <span className="font-mono font-semibold">{d.answered}/8</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {c.notStarted.length > 0 && (
        <p className="mt-3 text-xs text-stone-500" data-testid="not-started-count">
          {c.notStarted.length} {t("not started")}:{" "}
          {c.notStarted.map((d) => t(d.label)).join(", ")}.
        </p>
      )}

      {c.partial.length === 0 && c.notStarted.length === 0 && (
        <p className="mt-2 text-sm text-emerald-700">{t("Every scored domain is complete.")}</p>
      )}
    </Section>
  );
}
